import type { AiChatMessage } from '@/components/@IntranetLibrary/AiChatView';
import type {
  ApprovalChatMessage,
  ApprovalChatSession,
  ApprovalChatSuggestion,
} from '@/types/approvalChat';

/**
 * 결재 AI 어시스턴트 채팅의 화면 메시지 변환 — 훅(useApprovalChat)에서 떼어낸 순수 함수.
 * 공용 AiChatView 메시지 모델을 그대로 쓰고, 결재 전용 정보는 assistant `meta` 에 싣는다.
 */

/** 제안 카드의 로컬 상태. 서버에는 남지 않는다(새로고침하면 다시 카드만 보인다). */
export type SuggestionState = 'open' | 'accepted' | 'declined';

export type ApprovalChatAssistantMeta = {
  aiPipelineId: string; // 이력 항목은 '' (매칭 불필요)
  suggestion: ApprovalChatSuggestion | null;
  suggestionState?: SuggestionState;
  /**
   * 이 화면에서 방금 응답한 제안 — 결과는 뒤따르는 말풍선이 보여 주므로 카드는 숨긴다.
   * 이력에서 복원된 응답(서버 suggestionState)은 카드 안에 결과 한 줄로 보여 준다.
   */
  suggestionResolvedLocally?: boolean;
  /** 제안을 수락해 결재 처리한 결과 말풍선 — 「✓ 반려 완료」 배지 */
  doneLabel?: string;
};

export const metaOf = (m: AiChatMessage): ApprovalChatAssistantMeta | null =>
  m.kind === 'assistant' && m.meta
    ? (m.meta as ApprovalChatAssistantMeta)
    : null;

/** 서버 이력(최신순) → 화면용(과거→최신) */
export const mapHistory = (messages: ApprovalChatMessage[]): AiChatMessage[] =>
  [...messages].reverse().map((m): AiChatMessage => {
    if (m.role === 'user') {
      return { kind: 'user', id: m.id, text: m.content };
    }
    const meta: ApprovalChatAssistantMeta = {
      aiPipelineId: '',
      suggestion: m.suggestion,
      // 서버에 기록된 제안 응답이 있으면 그 상태로 복원 — 다시 열어도 「처리됨」으로 보인다
      suggestionState: m.suggestionState ?? 'open',
    };
    return {
      kind: 'assistant',
      id: m.id,
      text: m.content,
      status: 'done',
      meta,
    };
  });

/** 이력의 마지막 항목이 질문이면 아직 답변이 오지 않은 상태 */
export const endsWithUnanswered = (list: AiChatMessage[]) =>
  list[list.length - 1]?.kind === 'user';

/**
 * 복원 대상 = 가장 최근에 만들어진 세션. 목록 순서(갱신순)에 기대지 않고 createdAt 으로 고른다 —
 * 채팅 초기화로 세션이 늘어나므로, 갱신순이면 초기화 전 대화가 되살아난다.
 */
export const latestSessionId = (sessions: ApprovalChatSession[]) =>
  [...sessions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.id;

/**
 * 완료 이벤트 — aiPipelineId 가 같은 대기 말풍선을 답변으로 교체.
 * 서버 messageId 가 오면 말풍선 id 를 그것으로 바꿔, 이후 제안 응답 기록(PATCH)이 서버 메시지를 가리키게 한다.
 */
export const applyAnswer = (
  list: AiChatMessage[],
  aiPipelineId: string,
  answer: string,
  suggestion: ApprovalChatSuggestion | null,
  messageId?: string,
): AiChatMessage[] =>
  list.map((m) => {
    const meta = metaOf(m);
    if (!meta || meta.aiPipelineId !== aiPipelineId || m.kind !== 'assistant') {
      return m;
    }
    return {
      ...m,
      id: messageId || m.id,
      text: answer,
      status: 'done',
      meta: { ...meta, suggestion, suggestionState: 'open' },
    };
  });

/** 오류 이벤트 — aiPipelineId 가 같은 대기 말풍선을 오류로 */
export const applyError = (
  list: AiChatMessage[],
  aiPipelineId: string,
): AiChatMessage[] =>
  list.map((m) => {
    const meta = metaOf(m);
    return meta && meta.aiPipelineId === aiPipelineId && m.kind === 'assistant'
      ? { ...m, status: 'error' }
      : m;
  });

/** 제안 카드 상태 갱신 (수락/거절) — 이 화면에서 응답한 것이므로 카드는 숨기고 결과 말풍선이 뒤따른다 */
export const resolveSuggestion = (
  list: AiChatMessage[],
  messageId: string,
  state: SuggestionState,
): AiChatMessage[] =>
  list.map((m) => {
    const meta = metaOf(m);
    return meta && m.id === messageId && m.kind === 'assistant'
      ? {
          ...m,
          meta: {
            ...meta,
            suggestionState: state,
            suggestionResolvedLocally: true,
          },
        }
      : m;
  });

/** 서버에 기록할 수 있는 메시지인지 — 낙관적(tmp-)·로컬(local-) 말풍선은 서버 id 가 아니다 */
export const isServerMessageId = (id: string) =>
  !id.startsWith('tmp-') && !id.startsWith('local-');

/** 「○○에게 전달해」 요청 — 받는 사람을 문서 참조인으로 추가하고 참조 메일을 보낸다 (PORTAL-15736) */
export type ApprovalChatShareRequest = {
  names: string[];
  message: string;
};

const JSON_FENCE = /```(?:json)?[ \t]*\n([\s\S]*?)\n?```/g;

const toShareRequest = (raw: unknown): ApprovalChatShareRequest | null => {
  if (!raw || typeof raw !== 'object') return null;
  const share = (raw as { share?: unknown }).share;
  if (!share || typeof share !== 'object') return null;
  const { names, message } = share as { names?: unknown; message?: unknown };
  const list = (Array.isArray(names) ? names : [names])
    .filter((n): n is string => typeof n === 'string')
    .map((n) => n.trim())
    .filter(Boolean);
  if (list.length === 0) return null;
  return {
    names: Array.from(new Set(list)),
    message: typeof message === 'string' ? message.trim() : '',
  };
};

/**
 * 답변 본문에서 전달 블록(```json {"share": {...}}```)을 떼어 낸다.
 * 서버 파서는 맨 마지막 코드 블록이 결재 처리 제안일 때만 분리하므로, 전달 블록은 본문에 남아 화면이 직접 꺼낸다.
 * 전달 블록이 아닌 코드 블록(사용자가 요청한 JSON 등)은 그대로 둔다. 여러 개면 받는 사람을 합친다.
 */
export const splitShareRequest = (
  text: string,
): { body: string; share: ApprovalChatShareRequest | null } => {
  let share: ApprovalChatShareRequest | null = null;
  const body = text.replace(JSON_FENCE, (block, inner: string) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(inner);
    } catch {
      return block;
    }
    const found = toShareRequest(parsed);
    if (!found) return block;
    share = share
      ? {
          names: Array.from(new Set([...share.names, ...found.names])),
          message: share.message || found.message,
        }
      : found;
    return '';
  });
  return {
    body: share ? body.replace(/\n{3,}/g, '\n\n').trim() : text,
    share,
  };
};

/** AI 가 직전 확인 카드에 대한 사용자의 동의("ㅇㅇ", "진행해" 등)를 알아들었다는 신호 (PORTAL-15726) */
export type ApprovalChatDecisionConfirm = {
  action: 'approve' | 'reject' | 'share';
};

/**
 * 답변 본문에서 카드 실행 블록(```json {"decision": {"action": "reject"}}```)을 떼어 낸다.
 * 서버 파서는 최상위 action·suggestion 키가 있는 블록만 제안으로 분리하므로 이 블록은 본문에 남는다.
 */
export const splitDecisionConfirm = (
  text: string,
): { body: string; decision: ApprovalChatDecisionConfirm | null } => {
  let decision: ApprovalChatDecisionConfirm | null = null;
  const body = text.replace(JSON_FENCE, (block, inner: string) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(inner);
    } catch {
      return block;
    }
    const raw =
      parsed && typeof parsed === 'object'
        ? (parsed as { decision?: unknown }).decision
        : null;
    const action =
      raw && typeof raw === 'object'
        ? (raw as { action?: unknown }).action
        : null;
    if (action !== 'approve' && action !== 'reject' && action !== 'share') {
      return block;
    }
    decision = { action };
    return '';
  });
  return {
    body: decision ? body.replace(/\n{3,}/g, '\n\n').trim() : text,
    decision,
  };
};
